import { kindNamespace, type StoredEvent } from "@facade/dashboard-shared";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fmtTime, summarize } from "../lib/format";
import { EventDetail } from "./EventDetail";

export const NAMESPACES = ["run", "model", "screen", "graph", "loop", "feed", "capability", "harness", "device", "telemetry"];

export function nsColor(kind: string) {
  return `var(--ns-${kindNamespace(kind)}, var(--muted))`;
}

export function KindChips({ value, onChange, all }: { value: Set<string>; onChange: (v: Set<string>) => void; all?: string[] }) {
  const list = all ?? NAMESPACES;
  const toggle = (ns: string) => {
    const next = new Set(value);
    if (next.has(ns)) next.delete(ns);
    else next.add(ns);
    onChange(next);
  };
  return (
    <div className="chips">
      {list.map((ns) => (
        <button key={ns} className={`chip ${value.has(ns) ? "on" : ""}`} style={{ color: value.has(ns) ? `var(--ns-${ns}, var(--text))` : undefined }} onClick={() => toggle(ns)}>
          {ns}
        </button>
      ))}
      {value.size > 0 && (
        <button className="chip" onClick={() => onChange(new Set())}>
          clear
        </button>
      )}
    </div>
  );
}

export function EventRow({ e, deviceId, onOpenQuery, showRun = true }: { e: StoredEvent; deviceId?: string; onOpenQuery?: (e: StoredEvent) => void; showRun?: boolean }) {
  const [open, setOpen] = useState(false);
  const p = e.payload as any;
  const err = (e.kind === "run.end" && p?.ok === false) || (e.kind === "model.query" && p?.ok === false) || e.kind.endsWith(".quarantine");
  return (
    <div className={`ev ${open ? "open" : ""} ${err ? "err" : ""}`} style={{ ["--ns-color" as any]: nsColor(e.kind) }}>
      <span className="t" title={new Date(e.ts).toLocaleString()}>
        {fmtTime(e.ts)}
      </span>
      <span className="k" title={`#${e.id} seq ${e.seq}`} onClick={() => setOpen(!open)} style={{ cursor: "pointer" }}>
        {e.kind}
      </span>
      <span className="s" onClick={() => setOpen(!open)} style={{ cursor: "pointer" }}>
        {summarize(e.kind, p)}
        {showRun && e.runId && deviceId && (
          <>
            {" "}
            <Link to={`/d/${deviceId}/runs/${encodeURIComponent(e.runId)}`} className="muted mono" style={{ fontSize: 11 }} onClick={(ev) => ev.stopPropagation()}>
              ↗{e.runId.slice(0, 12)}
            </Link>
          </>
        )}
      </span>
      {open && (
        <div className="ev-detail">
          <EventDetail e={e} onOpenQuery={onOpenQuery} />
        </div>
      )}
    </div>
  );
}

export function EventStream({
  events,
  deviceId,
  onOpenQuery,
  newestFirst = true,
  ns,
}: {
  events: StoredEvent[];
  deviceId?: string;
  onOpenQuery?: (e: StoredEvent) => void;
  newestFirst?: boolean;
  ns?: Set<string>;
}) {
  const shown = useMemo(() => {
    const f = ns && ns.size ? events.filter((e) => ns.has(kindNamespace(e.kind))) : events;
    return newestFirst ? [...f].reverse() : f;
  }, [events, ns, newestFirst]);
  if (!shown.length) return <div className="empty">no events</div>;
  return (
    <div className="stream">
      {shown.map((e) => (
        <EventRow key={`${e.deviceId}|${e.seq}`} e={e} deviceId={deviceId} onOpenQuery={onOpenQuery} />
      ))}
    </div>
  );
}
